import { useEffect, useMemo, useState } from 'react'
import type { SampleBucket } from '../OperationTypes.js'
import type { VisualizationPipeline } from '../RunPipeline.js'
import { runPipeline } from '../RunPipeline.js'
import { BasicModeEditor } from './Widgets/BasicModeEditor.js'
import { CompactBasicModeEditor } from './Widgets/CompactBasicModeEditor.js'
import type { MetadataFields } from '../MetadataFields.js'
import { generateMetadataFields } from '../MetadataFields.js'
import type { VisualizationSampleContainer } from '../VisualizationSampleContainer.js'

export type Mode = 'full' | 'compact' | 'only-filter'

type GroupingPanelProps = {
  samples: VisualizationSampleContainer[]
  onFieldsChange?: (metadataFields: MetadataFields) => void
  onBucketsChange: (buckets: SampleBucket[], pipeline: VisualizationPipeline) => void
  onInitialBuckets?: (buckets: SampleBucket[], pipeline: VisualizationPipeline) => void
  initialPipeline?: VisualizationPipeline
  mode: Mode
}

const defaultPipeline: VisualizationPipeline = {
  filters: [],
  groupBys: [],
  projections: [],
  sortBys: []
}

export function GroupingPanel({
  samples,
  onFieldsChange,
  onBucketsChange,
  onInitialBuckets,
  initialPipeline,
  mode = 'full'
}: GroupingPanelProps) {
  const [pipeline, setPipeline] = useState<VisualizationPipeline>(initialPipeline ?? defaultPipeline)
  const metadataFields = useMemo(() => generateMetadataFields(samples), [samples])
  const buckets = useMemo(() => runPipeline(samples, pipeline, metadataFields), [samples, pipeline, metadataFields])
  useEffect(() => {
    if (onInitialBuckets) {
      onInitialBuckets(buckets, pipeline)
    }
  }, [])

  useEffect(() => {
    if (onFieldsChange) {
      onFieldsChange(metadataFields)
    }
  }, [metadataFields])

  // Automatically recompute buckets and call on change
  useEffect(() => {
    onBucketsChange(buckets, pipeline)
  }, [buckets, pipeline])

  if (mode === 'full') {
    return (
      <div className="flex flex-col gap-4 p-4 bg-base-200 rounded-sm shadow-md w-full">
        <h3 className="font-semibold text-lg">Grouping Configuration</h3>
        <BasicModeEditor metadataFields={metadataFields} samples={samples} pipeline={pipeline} setPipeline={setPipeline} />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 w-full">
      <CompactBasicModeEditor metadataFields={metadataFields} samples={samples} pipeline={pipeline} setPipeline={setPipeline} mode={mode} />
    </div>
  )
}
